'use client';
import React from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import FractionDisplay from '@src/components/Shared/FractionDisplay';
import NextLink from '@src/components/Shared/NextLink';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { SpeedCardPropTypes } from './SpeedCard.types';

// IDs here must match the measurementUnit state
const units: UnitTypes[] = [
  { id: 'meters', name: 'Meters', unit: 'm' },
  { id: 'yards', name: 'Yards', unit: 'yd' },
];

const SpeedCard: React.FC<SpeedCardPropTypes> = ({}) => {
  const isMobile = useIsMobile();

  // State
  const [measurementUnit, setMeasurementUnit] = React.useState<'meters' | 'yards'>('meters');
  const [distance, setDistance] = React.useState('');
  const [splitTime, setSplitTime] = React.useState('');
  const [tempDistance, setTempDistance] = React.useState('');
  const [tempSplitTime, setTempSplitTime] = React.useState('');

  const handleCalculate = () => {
    setDistance(tempDistance);
    setSplitTime(tempSplitTime);
  };
  const hasPendingChanges = isMobile && (tempDistance !== distance || tempSplitTime !== splitTime);

  // Parse inputs
  const d = parseFloat(distance) || 0;
  const t = parseFloat(splitTime) || 0;
  const baseSpeed = t > 0 ? d / t : 0; // in m/s or yd/s

  // Speed per second
  const speedPerSecond = baseSpeed;
  const perSecondUnit = measurementUnit === 'meters' ? 'm/s' : 'yd/s';

  // Speed per hour
  let speedPerHour: number;
  let perHourUnit: string;

  if (measurementUnit === 'meters') {
    // m/s → km/h
    speedPerHour = baseSpeed * 3.6;
    perHourUnit = 'km/h';
  } else {
    // yd/s → mph
    // 1 mile = 1760 yards, 3600 seconds per hour
    speedPerHour = (baseSpeed * 3600) / 1760;
    perHourUnit = 'mph';
  }

  const formattedSpeedPerHour = speedPerHour.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const formattedSpeedPerSecond = speedPerSecond.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // --- Debounce the formattedSpeedPerHour by 3000 ms ---
  const debouncedSpeedPerHour = useDebounce(formattedSpeedPerHour, 3000);

  // Fire a tracking event whenever the debounced speed is valid
  React.useEffect(() => {
    if (
      distance !== '' &&
      splitTime !== '' &&
      !isNaN(Number(debouncedSpeedPerHour)) &&
      isFinite(Number(debouncedSpeedPerHour))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_speed',
          value: 1,
        });
      }
    }
    // Only re-run when debouncedSpeedPerHour changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSpeedPerHour]);

  return (
    <CardMetric
      id='calculator-speed'
      title='Speed'
      productIcons={[
        {
          alt: 'SmartSpeed App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/Dg2ZJXDIhN88rg19CH7JT/53585f72a882a38c9b9862986b7327e3/smartspeed-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2.2rem] text-[1.2rem]'>
        Input split times for a given distance to determine <strong>average speed</strong>.
      </p>

      <FractionDisplay
        className='mb-[2rem]'
        label='Speed'
        numerator='<strong className="font-medium">Distance</strong> (between timing gates)'
        denominator='<strong className="font-medium">Time</strong> (in seconds)'
      />

      <UnitDropdown
        className='mb-[2.4rem]'
        units={units}
        activeUnitId={measurementUnit}
        onChange={(id) => setMeasurementUnit(id as 'meters' | 'yards')}
      />

      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Distance', 'Split Distance']}
        units={[
          {
            id: measurementUnit,
            name: measurementUnit === 'meters' ? 'm' : 'yd',
            unit: '',
          },
        ]}
        activeUnit={measurementUnit}
        value={isMobile ? tempDistance : distance}
        onUnitSelect={() => {}}
        onChange={(e) => (isMobile ? setTempDistance(e.target.value) : setDistance(e.target.value))}
      />

      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Time', 'Split Time']}
        units={[{ id: 'seconds', name: 'Seconds', unit: 's' }]}
        activeUnit='seconds'
        value={isMobile ? tempSplitTime : splitTime}
        onUnitSelect={() => {}}
        onChange={(e) =>
          isMobile ? setTempSplitTime(e.target.value) : setSplitTime(e.target.value)
        }
      />

      {/* Two dynamic speed displays */}
      <div className='mb-[3.1rem] flex items-center text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>Speed</span>

        {/* per-hour */}
        <div className='ml-[2rem] flex items-baseline border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] pr-[2rem]'>
          <span className='text-[1.8rem] font-normal sm:text-[3rem]'>{formattedSpeedPerHour}</span>
          <span className='text-[1.6rem] font-light'>{perHourUnit}</span>
        </div>

        {/* per-second */}
        <div className='ml-[2rem] flex items-baseline border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] pr-[2rem]'>
          <span className='text-[1.8rem] font-normal sm:text-[3rem]'>{formattedSpeedPerSecond}</span>
          <span className='text-[1.6rem] font-light'>{perSecondUnit}</span>
        </div>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='w-full text-[1.2rem] md:flex md:items-center md:gap-[1.6rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            Find more information on{' '}
            <NextLink
              className='font-light underline'
              href='https://support.vald.com/hc/en-au/articles/15854427158041-SmartSpeed-Plus-Set-up-your-timing-gates'
            >
              setting up
            </NextLink>{' '}
            your timing gates for a flying 10m sprint.
          </p>
        </div>

        {isMobile && (
          <button
            onClick={handleCalculate}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
            disabled={!hasPendingChanges}
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default SpeedCard;
